import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, Building2, Users, Globe2, Scale } from "lucide-react";
import { SLUG, PRACTICE_GROUPS, TEAM, ABOUT } from "./aboData";
import AboLayout, { INK, TEAL, LIGHT, SLATE } from "./AboLayout";

const fadeUp = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, margin: "-60px" }, transition: { duration: 0.5, ease: "easeOut" } };

const initialsOf = (name) => name.replace(/^Atty\.\s*/, "").split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]).join("");

const STAT_TILES = [
  { icon: Building2, label: "Founded", value: "2018", note: "in Bonifacio Global City" },
  { icon: Globe2, label: "Network", value: "Mackrell Int'l", note: "60 countries · 4,500 lawyers" },
  { icon: Scale, label: "Practice Groups", value: "5", note: "litigation to fintech regulatory" },
  { icon: Users, label: "Team", value: "11–50", note: "employees per public records" },
];

export default function AboHome() {
  const basePath = `/${SLUG}`;
  return (
    <AboLayout active="home">
      {/* HERO — bento-style stat grid instead of a paragraph, matching a "technology-aware" firm */}
      <section className="py-20 sm:py-24" style={{ background: INK }} data-testid="abo-hero">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 grid lg:grid-cols-[1.2fr_1fr] gap-10 items-center">
          <div>
            <motion.span {...fadeUp} className="inline-block text-xs uppercase tracking-[0.25em] font-semibold" style={{ color: "#5EEAD4" }}>Bonifacio Global City, Taguig</motion.span>
            <motion.h1 {...fadeUp} className="mt-5 text-4xl sm:text-6xl font-extrabold text-white leading-[1.05]">Counsel for Growing Businesses</motion.h1>
            <motion.div {...fadeUp} className="mt-9 flex flex-wrap gap-4">
              <a href={`${basePath}/contact`} data-testid="abo-hero-cta" className="rounded-md px-7 py-3.5 text-sm font-semibold" style={{ background: TEAL, color: "white" }}>Request a Consultation</a>
              <a href={`${basePath}/practice-areas`} data-testid="abo-hero-practice" className="inline-flex items-center gap-2 rounded-md px-7 py-3.5 text-sm font-semibold text-white border border-white/25">Practice Areas <ArrowRight size={16} /></a>
            </motion.div>
          </div>
          <motion.div {...fadeUp} className="grid grid-cols-2 gap-3">
            {STAT_TILES.map((t) => (
              <div key={t.label} className="rounded-lg p-4" style={{ background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)" }}>
                <t.icon size={18} style={{ color: TEAL }} />
                <p className="mt-2 text-lg font-bold text-white leading-tight">{t.value}</p>
                <p className="text-[11px] text-white/50">{t.note}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      <section className="py-16" style={{ background: LIGHT }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <motion.h2 {...fadeUp} className="text-2xl sm:text-3xl font-extrabold text-center mb-10" style={{ color: INK }}>Practice Areas</motion.h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {PRACTICE_GROUPS.map((g) => (
              <motion.div key={g.title} {...fadeUp} className="p-5 bg-white rounded-lg shadow-sm border-t-4" style={{ borderColor: TEAL }}>
                <h3 className="font-bold text-sm" style={{ color: INK }}>{g.title}</h3>
                <ul className="mt-2 space-y-1">
                  {g.areas.slice(0, 2).map((a) => <li key={a} className="text-xs" style={{ color: SLATE }}>{a}</li>)}
                </ul>
              </motion.div>
            ))}
          </div>
          <motion.div {...fadeUp} className="text-center mt-8">
            <a href={`${basePath}/practice-areas`} data-testid="abo-see-all-practice" className="inline-flex items-center gap-2 text-sm font-semibold" style={{ color: TEAL }}>See full practice-area architecture <ArrowRight size={15} /></a>
          </motion.div>
        </div>
      </section>

      {/* TEAM TEASER */}
      <section className="py-16" style={{ background: "white" }}>
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <motion.h2 {...fadeUp} className="text-2xl sm:text-3xl font-extrabold text-center mb-10" style={{ color: INK }}>Our Team</motion.h2>
          <div className="grid sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {TEAM.map((t) => (
              <motion.div key={t.name} {...fadeUp} className="text-center">
                {t.photo ? (
                  <img src={t.photo} alt={t.name} className="w-full aspect-[3/4] object-cover object-top rounded-lg shadow-sm" />
                ) : (
                  <div className="w-full aspect-[3/4] rounded-lg flex items-center justify-center" style={{ background: LIGHT }}>
                    <span className="text-3xl font-bold" style={{ color: TEAL }}>{initialsOf(t.name)}</span>
                  </div>
                )}
                <p className="mt-2 text-xs font-semibold leading-tight" style={{ color: INK }}>{t.name}</p>
                <p className="text-[11px]" style={{ color: SLATE }}>{t.role}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* MACKRELL AFFILIATION */}
      <section className="py-14" style={{ background: LIGHT }}>
        <div className="max-w-3xl mx-auto px-5 sm:px-8 text-center">
          <img src="/law/abo-penaranda-law-bgc-concept/mackrell-badge.jpg" alt="Member of Mackrell International — 60 countries, 4500 lawyers" className="h-20 mx-auto mb-5 object-contain" />
          <motion.p {...fadeUp} className="text-sm" style={{ color: SLATE }}>{ABOUT.affiliation}</motion.p>
        </div>
      </section>

      <section className="py-10" style={{ background: INK }}>
        <div className="max-w-2xl mx-auto px-5 sm:px-8 flex items-start gap-3 text-xs text-white/60">
          <ShieldCheck size={16} className="shrink-0 mt-0.5" style={{ color: "#5EEAD4" }} />
          This concept presents only verified people and facts — see Our Team and About for details.
        </div>
      </section>
    </AboLayout>
  );
}
